import type * as React from "react";

import { Typography, type TypographyProps } from "@/components/ui/typography";

import { cn } from "@/lib/utils";

export const headingVariantDefaults = {
  page: {
    as: "h1",
    size: "5xl",
    weight: "normal",
    leading: "tight",
    tracking: "tight",
    className: "text-slate-900 md:text-4xl lg:leading-[1.1]",
  },
  section: {
    as: "h2",
    size: "2xl",
    weight: "normal",
    tracking: "tight",
    className: "mb-2",
  },
  subsection: {
    as: "h3",
    size: "lg",
    weight: "normal",
    leading: "none",
    tracking: "tight",
  },
  group: {
    as: "div",
    size: "base",
    weight: "semibold",
  },
  panel: {
    as: "h2",
    size: "lg",
    weight: "semibold",
    className: "mb-4 text-gray-900",
  },
  card: {
    as: "h3",
    weight: "semibold",
    leading: "none",
    tracking: "tight",
  },
  dialog: {
    as: "h2",
    size: "lg",
    weight: "semibold",
    leading: "none",
    tracking: "tight",
  },
} as const satisfies Record<string, TypographyProps>;

type HeadingVariant = keyof typeof headingVariantDefaults;

type HeadingTypographyProps = Omit<
  TypographyProps,
  | "size"
  | "weight"
  | "tracking"
  | "leading"
  | "decoration"
  | "balance"
  | "className"
>;

export interface HeadingProps extends HeadingTypographyProps {
  variant: HeadingVariant;
}

export function headingTypographyProps(
  variant: HeadingVariant,
  className?: string,
): TypographyProps {
  const defaults = headingVariantDefaults[variant];

  return {
    ...defaults,
    className: cn(
      "className" in defaults ? defaults.className : undefined,
      className,
    ),
  };
}

const Heading = ({
  ref,
  variant,
  ...props
}: HeadingProps & { ref?: React.Ref<HTMLElement> }) => {
  return (
    <Typography
      data-slot="heading"
      ref={ref}
      {...headingTypographyProps(variant)}
      {...props}
    />
  );
};
Heading.displayName = "Heading";

export { Heading, type HeadingVariant };
